import type { Action } from 'redux';
import type { ThunkAction, ThunkDispatch } from 'redux-thunk';
import type displayValues from '@/resources/displayValues.json';

export enum ApiActionStatus {
    NOT_STARTED = 'NOT_STARTED',
    PENDING = 'PENDING',
    ERROR = 'ERROR',
    OK = 'OK',
}

export type PaleVertical = 'pp' | 'lms';

export type PaleState = {
    hotelResults: HotelOffersResponse;
    hotel: HotelResponse;
    results: OffersResponse;
    filters: Filters;
    appState: AppState;
};

export type OfferResponse = {
    offerId: string;
    supplier: string;
    deepLink: string;
    originAirportCode: string;
    pricePerPerson: number;
    fromPage?: string;
    country: string;
    region: string;
    destination: string;
    departureDate: string;
    returnDate: string;
    numberOfAdults?: string;
    agesOfChildren?: string;
};

export type OffersResponse = {
    numberOfHotels: number;
    totalPages: number;
    currentPage: number;
    offers: Offer[];
    facets: Facets;
    sortOptions: SortValue[];
    defaultSort: SortValue;
    status: ApiActionStatus;
};

export type HotelResponse = {
    id: string;
    name: string;
    tripAdvisorId: string;
    rating: number;
    country: string;
    region: string;
    destination: string;
    facilities: string[];
    activities: string[];
    distanceToBeach: number;
    distanceToCity: number;
    imagePaths: string[];
};

export type HotelOffersResponse = {
    numberOfOffers: number;
    totalPages: number;
    currentPage: number;
    offers: HotelOffer[];
    sortOptions: string[];
    defaultSort: string;
    status: ApiActionStatus;
};

export type AppState = {
    filtersOpen: boolean;
    isMapMode: boolean;
};

export type Offer = {
    offerId: string;
    supplier: string;
    country: string;
    region: string;
    destination: string;
    originAirportCode: string;
    destinationAirportCode: string;
    rating: number;
    board?: string;
    facilities: string[];
    activities: string[];
    distanceToBeach: number;
    distanceToCity: number;
    imagePaths: string[];
    hotelId: string;
    hotelName: string;
    hotelTripAdvisorId?: string;
    duration: number;
    departureDate: string;
    departureDateArrival?: string;
    departureStopovers?: number;
    returnDate: string;
    returnDateArrival?: string;
    returnDateStopovers?: number;
    pricePerPerson: number;
    priceTotal?: number;
    numberOfAdults?: number;
    numberOfChildren?: number;
    deepLink: string;
    latitude?: number;
    longitude?: number;
};

export type HotelOffer = {
    offerId: string;
    supplier: string;
    originAirport: string;
    destinationAirport: string;
    duration: number;
    departureDate: string;
    departureDateArrival?: string;
    departureStopovers?: number;
    departureTravelDuration?: number;
    returnDate: string;
    returnDateArrival?: string;
    returnStopovers?: number;
    returnTravelDuration?: number;
    flightDetails?: FlightDetails;
    pricePerPerson: number;
    priceTotal?: number;
    board?: string;
    roomDescription: string;
    deepLink: string;
    numberOfAdults?: number;
    numberOfChildren?: number;
};

export type FlightDetails = {
    outbound: Flight[];
    inbound: Flight[];
};

export type Flight = {
    airlineCode?: string;
    airline?: string;
    flightNo?: string;
    originAirport: string;
    destinationAirport: string;
    departureTime: string;
    arrivalTime: string;
    flightDuration: number;
};

export type SortValue = keyof typeof displayValues.sorting;

export type Facets = {
    location: NestedFacet[];
    originAirport: Facet[];
    charterSuppliers: Facet[];
    nonCharterSuppliers: Facet[];
    activities: Facet[];
    facilities: Facet[];
    meals: Facet[];
};

export type Facet = {
    value: string;
    count?: number;
};

export type NestedFacet = {
    value: string;
    count?: number;
    children?: NestedFacet[];
};

export type CheckboxFilters = 'meals' | 'facilities' | 'activities' | 'suppliers' | 'rating';

export type Filters = {
    sort?: SortValue;
    page?: string;
    origin: string;
    destinations: Destinations;
    meals: CheckboxMap;
    facilities: CheckboxMap;
    activities: CheckboxMap;
    priceRange?: NumberRange;
    keywords: string;
    suppliers: CheckboxMap;
    distanceToBeach?: NumberRange;
    distanceToCity?: NumberRange;
    rating: CheckboxMap;
    travelMonths: Month[];
    departureDate?: FlexibleDate;
    returnDate?: FlexibleDate;
    duration?: NumberRange;
    hotelId?: string;
    numberOfAdults: number;
    agesOfChildren: number[];
    ignoredFilters: (keyof Filters)[];
    boundingBox?: BoundingBox;
    destinationLabel?: string;
};

export type Destinations = {
    list: string[];
    tree: CheckboxTree;
};

export type CheckboxMap = {
    [key: string]: boolean;
};

export type CheckboxTree = {
    [key: string]: CheckboxTree;
};

export type NumberRange = [number | null, number | null];

export type FlexibleDate = {
    date?: string;
    flexibility?: number;
};

export type FlexibilityOption = {
    value: number;
    text: string;
};

export type Month = {
    year: number;
    month: number;
};

export type EnrichedFlight = {
    flight: Flight;
    index: number;
    parsedDepartureTime?: Date;
    parsedArrivalTime?: Date;
    stopoverMinutes?: number;
    showStopoverAlert?: boolean;
    stopoverText?: string;
};

export type LegConfig = {
    key: 'outbound' | 'inbound';
    isReturn: boolean;
    summaryDepartureISO?: string;
    summaryArrivalISO?: string;
    summaryDuration?: number;
    stopovers?: number;
    origin: string;
    destination: string;
    detailedFlights: Flight[];
};

export type LegWithData = LegConfig & {
    detailed: EnrichedFlight[];
    summaryDepartureParsed?: Date;
    summaryArrivalParsed?: Date;
    headingDate: Date;
    hasDetailed: boolean;
};

export type LegWithRows = LegWithData & {
    rows: EnrichedFlight[];
};

export type FlightsResult = {
    legsWithRows: LegWithRows[];
    showUnknownStopoversWarning: boolean;
    hasFlightDetails: boolean;
};

export type ApiQueryParams = {
    sort?: string;
    page?: string;
    from?: string;
    to?: string[] | string;
    meals?: string[] | string;
    facilities?: string[] | string;
    activities?: string[] | string;
    price?: string;
    q?: string;
    suppliers?: string[] | string;
    beach?: string;
    city?: string;
    rating?: string[] | string;
    travelMonths?: string[] | string;
    duration?: string;
    departureDate?: string;
    returnDate?: string;
    hotelId?: string;
    numberOfAdults?: string;
    agesOfChildren?: string;
    bbox?: string;
};

export interface BoundingBox {
    minLat: number;
    minLon: number;
    maxLat: number;
    maxLon: number;
}

export type OverrideValues<K, T> = { [P in keyof K]: T };

export type ThunkResult<R> = ThunkAction<R, PaleState, undefined, Action>;
export type ThunkDispatchFunc = ThunkDispatch<PaleState, undefined, Action>;

export const PRICE_MIGHT_VARY_WARNING =
    'Prisene kan variere for barn under 2 år og over 12 år. Husk å sjekke alder og reisefølge hos leverandøren.';
